import { Flex } from 'antd';
import Button from 'components/Button';

import Table from 'components/Table';
import FeedbackDropdown from 'components/FeedbackDropdown';
import CustomLoader from 'components/CustomLoader';

import { isMobileScreenSize, renderDate, renderInfoValue } from 'utils/helpers';
import {
  PICTURE_APPROVAL_REASONS,
  PICTURE_REJECTION_REASONS,
  PIPE_REVIEW_STATUS,
} from 'utils/constants';
import { Document, PipeReview, TablePagination } from 'types/common';
import { ColumnsType } from 'antd/es/table';
import CustomFileList from 'components/CustomFileList';
import PipeDetailContent from './PipeDetailContent';
import StatusTag from 'components/StatusTag';
import { getPipeDate, getPipePictureList, getPipeStatus } from '../helper';
import { useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import useTrackerWrapper from 'hooks/useTracker';

type PipeReviewTableProps = {
  data?: PipeReview[];
  isLoading: boolean;
  pagination: TablePagination;
  isDocumentActionLoading: boolean;
  onDocumentAction: (
    id: string,
    task: string,
    status: 'APPROVED' | 'REJECTED',
    reason: string,
  ) => void;
  highlighted?: string | null;
};

type PipeTableData = PipeReview & { pipeId: string };

const PipeReviewTable = ({
  data,
  isLoading,
  pagination,
  isDocumentActionLoading,
  onDocumentAction,
  highlighted,
}: PipeReviewTableProps) => {
  const [openDetailOfId, setOpenDetailOfId] = useState('');
  const { onClickWrapper } = useTrackerWrapper();
  /*  Pipe Data from API can have identical ID value, so in this
      case, the data need to have a new separate ID for table usage */
  const formatData: PipeTableData[] | undefined = data?.map((val, index) => ({
    ...val,
    id: `${val.id}${index}`,
    pipeId: val.id,
  }));

  const renderChangeStatus = (id: string, data: PipeTableData) => {
    const status = getPipeStatus(data);

    if (isDocumentActionLoading) {
      return (
        <Flex gap={10} className="ms-2" align="center">
          <CustomLoader size="small" />
        </Flex>
      );
    }

    return (
      <Flex gap={10} align="center">
        {status !== PIPE_REVIEW_STATUS.APPROVED &&
        status !== PIPE_REVIEW_STATUS.REJECTED ? (
          <>
            <FeedbackDropdown
              label={'Approve Picture'}
              className="table-action-btn approve"
              options={PICTURE_APPROVAL_REASONS}
              onMenuClick={(reason) =>
                onDocumentAction(
                  data.pipeId,
                  data.dryingEventTaskName,
                  'APPROVED',
                  reason,
                )
              }
            />
            <FeedbackDropdown
              label={'Reject Picture'}
              options={PICTURE_REJECTION_REASONS}
              className="table-action-btn reject"
              onMenuClick={(reason) =>
                onDocumentAction(
                  data.pipeId,
                  data.dryingEventTaskName,
                  'REJECTED',
                  reason,
                )
              }
            />
          </>
        ) : (
          ''
        )}
      </Flex>
    );
  };

  const handleClickDetail = (id: string) => {
    onClickWrapper('Pipe Review Detail', () => {
      setOpenDetailOfId(id);
    });
  };

  const renderTableHeader: ColumnsType<PipeTableData> = [
    {
      title: 'Farmer Group',
      dataIndex: ['cube', 'name'],
      onCell: (data) => ({
        onClick: () => handleClickDetail(data.id),
      }),
      className: 'clickable',
      render: (val) => (
        <Flex align="center" justify="space-between" gap={4}>
          {val ?? 'N/A'}
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      key: 'farmerGroup',
      width: 200,
    },
    {
      title: 'Agronomist Name',
      dataIndex: ['agronomist', 'name'],
      render: (agronomistName) => renderInfoValue(agronomistName, 'N/A'),
      key: 'agronomistName',
      width: 200,
    },
    {
      title: 'Pipe Number',
      dataIndex: 'name',
      render: (agronomistName) => renderInfoValue(agronomistName, 'N/A'),
      key: 'name',
      width: 180,
    },
    {
      title: 'Pipe Picture',
      dataIndex: 'documentList',
      render: (documentList: Document[], pipeData) => (
        <CustomFileList
          open={openDetailOfId === pipeData.id}
          /* Will only show non rejected pictures if status is not rejected */
          images={(getPipeStatus(pipeData) !== PIPE_REVIEW_STATUS.REJECTED
            ? getPipePictureList(pipeData)?.nonRejectedPictures
            : documentList
          )?.map((image) => image.url)}
          hasDrawer
          drawerTitle="Pipe Details"
          drawerContent={<PipeDetailContent data={pipeData} />}
          onClose={() => setOpenDetailOfId('')}
        />
      ),
      key: 'pipePicture',
      width: 350,
    },
    {
      title: 'Pipe Status',
      dataIndex: 'dryingEventTaskName',
      render: (_, data) => <StatusTag status={getPipeStatus(data)} />,
      key: 'pipeStatus',
      width: 200,
    },
    {
      title: 'Task',
      dataIndex: 'dryingEventTaskName',
      render: (task) => renderInfoValue(task, 'N/A'),
      key: 'dryingEventTaskName',
      width: 180,
    },
    {
      title: 'Submitted Date',
      dataIndex: 'id',
      render: (_, data) => renderDate(getPipeDate(data)),
      key: 'submittedDate',
      width: 180,
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'id',
      render: (id, data) => renderChangeStatus(id, data),
      width: 350,
      align: 'left',
    },
  ];

  return (
    <Table
      loading={isLoading}
      pagination={pagination}
      tableColumns={formatData}
      tableHeader={renderTableHeader}
      rowKey="id"
      highlighted={highlighted}
    />
  );
};

export default PipeReviewTable;
